/**
 * Phase 20 / Task 20.7 — DEC-256's rate tier, as the wizard shows it.
 *
 * The rate templates (T13 Service charge rates, T14 Customer rates) are refused
 * at commit with a 403 for any role without `MANAGE_SERVICE_CHARGES` or
 * `MANAGE_ALL_TENANTS` — today that means a Dispatch Manager can run every other
 * template and not these two (`api/src/routes/imports/rate-tier.ts`). The re-import
 * default is UPDATE, so the same permission could overwrite every rate the
 * tenant bills with.
 *
 * The API is the gate. This module only decides what the hauler is TOLD: the
 * preview step names the rate sheets and who can import them, before the click
 * that would be refused, and a 403 from the commit gets the same sentence rather
 * than a bare "permission denied". The permissions are read from the shared
 * role matrix, not restated here, so a change to the matrix moves both.
 */

import { ROLE_PERMISSIONS } from '@lodex-pro/shared/auth/permissions';
import type { RoleCode } from '@lodex-pro/shared/auth/role-codes';

import { familyLabel } from './mapping-state';

/** Mirrors `RATE_TEMPLATE_FAMILIES` in the API's rate-tier guard. */
export const RATE_FAMILIES: readonly string[] = ['SERVICE_CHARGE_RATES', 'CUSTOMER_RATES'];

function isRoleCode(role: string): role is RoleCode {
  return Object.prototype.hasOwnProperty.call(ROLE_PERMISSIONS, role);
}

/** Does this role hold the tier? An unknown role does not. */
export function canImportRates(role: string | null): boolean {
  if (role === null || !isRoleCode(role)) return false;
  const held: ReadonlySet<string> = ROLE_PERMISSIONS[role];
  return held.has('MANAGE_SERVICE_CHARGES') || held.has('MANAGE_ALL_TENANTS');
}

/** The rate families among `families`, in the order given. */
export function rateFamiliesIn(families: readonly string[]): readonly string[] {
  return families.filter((f) => RATE_FAMILIES.includes(f));
}

/** "Service charge rates and Customer rates" */
function listLabels(families: readonly string[]): string {
  const labels = families.map(familyLabel);
  if (labels.length <= 1) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1] ?? ''}`;
}

/** The one sentence, used on the preview and for a 403 at commit. */
export function rateRefusalMessage(families: readonly string[]): string {
  const named =
    families.length > 0 ? listLabels(families) : 'Service charge rates or Customer rates';
  return (
    `Only a tenant admin can import rates, and this file includes ${named}. ` +
    'Ask a tenant admin to run this import, or upload the file again and choose ' +
    '“Skip this sheet” for the rate sheets.'
  );
}
